import { TIME_WINDOWS } from './environment-config.tsx';
import type { LinkType } from './link-detector.tsx';
import { TRAIT_NAMES_STATE, TRAIT_NAMES_HARD_DISMISS } from './traits-constants.tsx';
import type { DismissState3pIn1p } from './spotlight/spotlightHelpers.ts';

/**
 * Integration state structure (internal representation)
 */
export type IntegrationState = {
	stage: number;
	lastShownTimestamp: number;
	hardDismiss: boolean;
};

/**
 * Traits structure for integration spotlight (TWO-TRAIT SYSTEM)
 * Each integration has TWO traits:
 * - State trait: JSON format with stage and timestamp (e.g., '{"stage":1,"timestamp":1704512000000}')
 * - Hard dismiss trait: 'true' or 'false'
 * Example: {
 *   'confluence_ui_integrationspotlightshownforslack_viewed_changed': '{"stage":1,"timestamp":1704512000000}',
 *   'confluence_ui_integrationspotlightdismissedforslack_clicked_changed': 'false'
 * }
 */
export type IntegrationSpotlightTraits = {
	[key: string]: string; // Trait name -> value
};

/**
 * Map of link types to their STATE trait names
 */
const STATE_TRAIT_MAP: Record<LinkType, string> = {
	slack: TRAIT_NAMES_STATE.SLACK,
	microsoft: TRAIT_NAMES_STATE.TEAMS,
	other: '', // Not used for 'other' type
};

/**
 * Map of link types to their HARD_DISMISS trait names
 */
const HARD_DISMISS_TRAIT_MAP: Record<LinkType, string> = {
	slack: TRAIT_NAMES_HARD_DISMISS.SLACK,
	microsoft: TRAIT_NAMES_HARD_DISMISS.TEAMS,
	other: '', // Not used for 'other' type
};

/**
 * Get STATE trait name for a specific link type
 */
const getStateTraitNameForType = (linkType: LinkType): string => {
	return STATE_TRAIT_MAP[linkType];
};

/**
 * Get HARD_DISMISS trait name for a specific link type
 */
const getHardDismissTraitNameForType = (linkType: LinkType): string => {
	return HARD_DISMISS_TRAIT_MAP[linkType];
};

/**
 * Serialize STATE (stage + timestamp) for trait storage using JSON
 * Format: JSON string with stage and timestamp
 * Example: '{"stage":1,"timestamp":1704512000000}'
 */
export const serializeStateForTrait = (stage: number, timestamp: number): string => {
	return JSON.stringify({ stage, timestamp });
};

/**
 * Serialize HARD_DISMISS status for trait storage
 * Returns: 'true' or 'false'
 */
export const serializeHardDismissForTrait = (hardDismiss: boolean): string => {
	// Serialized as JSON to keep parity with analytics attribute `traitValue`
	// (e.g. '{"hardDismiss":true}')
	return JSON.stringify({ hardDismiss });
};

/**
 * Deserialize STATE trait value from JSON format
 * Format: JSON string with stage and timestamp
 * Returns default (stage 0, timestamp 0) if parsing fails or trait is missing
 *
 * Stage 0 = never shown before (first-time user)
 * Stage 1 = shown once
 * Stage 2 = shown twice
 * etc.
 */
const deserializeStateTrait = (
	traitValue: string | null | undefined,
): { stage: number; lastShownTimestamp: number } => {
	if (!traitValue) {
		// First-time user: stage 0 (never shown), will be eligible immediately
		return { stage: 0, lastShownTimestamp: 0 };
	}

	try {
		const parsed = JSON.parse(traitValue);
		return {
			stage: parsed.stage || 0,
			lastShownTimestamp: parsed.timestamp || 0,
		};
	} catch (error) {
		return { stage: 0, lastShownTimestamp: 0 };
	}
};

/**
 * Deserialize HARD_DISMISS trait value
 * Returns: boolean (defaults to false if missing)
 */
const deserializeHardDismissTrait = (traitValue: string | null | undefined): boolean => {
	// Always default to false if not present
	if (!traitValue) {
		return false;
	}

	try {
		const parsed = JSON.parse(traitValue);
		return parsed?.hardDismiss === true;
	} catch (e) {
		return false;
	}
};

/**
 * Get integration state from traits (deserializes TWO separate traits)
 * Combines STATE trait and HARD_DISMISS trait into a single IntegrationState object
 */
export const getIntegrationState = (
	linkType: LinkType,
	traits: IntegrationSpotlightTraits,
): IntegrationState => {
	const stateTraitName = getStateTraitNameForType(linkType);
	const hardDismissTraitName = getHardDismissTraitNameForType(linkType);

	const stateValue = traits[stateTraitName];
	const hardDismissValue = traits[hardDismissTraitName];

	const { stage, lastShownTimestamp } = deserializeStateTrait(stateValue);
	const hardDismiss = deserializeHardDismissTrait(hardDismissValue);

	return {
		stage,
		lastShownTimestamp,
		hardDismiss,
	};
};

/**
 * Check if integration is eligible to show based on traits
 * @param linkType - The type of link to check eligibility for
 * @param traits - The user's integration spotlight traits
 * @param now - Current timestamp in milliseconds (defaults to Date.now())
 */
export const isIntegrationEligible = (
	linkType: LinkType,
	traits: IntegrationSpotlightTraits,
	now: number = Date.now(),
): boolean => {
	const state = getIntegrationState(linkType, traits);

	// 1. Hard dismiss check
	if (state.hardDismiss) {
		return false;
	}

	// 2. Stage-based time window check
	const timeSinceLastShown = now - state.lastShownTimestamp;

	// First time showing (stage 0) - always eligible
	if (state.stage === 0) {
		return true;
	}

	// Stage 1: Need 7 days before showing 2nd nudge
	if (state.stage === 1 && timeSinceLastShown < TIME_WINDOWS.STAGE_1_TO_2) {
		return false;
	}

	// Stage 2: Need 30 days before showing 3rd nudge
	if (state.stage === 2 && timeSinceLastShown < TIME_WINDOWS.STAGE_2_TO_3) {
		return false;
	}

	// Stage 3+: Need 90 days before showing next nudge
	if (state.stage >= 3 && timeSinceLastShown < TIME_WINDOWS.STAGE_3_PLUS) {
		return false;
	}

	// Passed all checks - eligible to show
	return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// 3P-within-1P invite spotlight trait helpers
// These are intentionally separate from the LinkType-based integration helpers
// above so that IntegrationLinkSpotlightComponent behaviour is unaffected.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The two link categories for the 3P-within-1P invite spotlight.
 * Intentionally separate from LinkType to avoid coupling the two flows.
 */
export type ThirdPartyInviteType = 'slack' | 'mailto';

const INVITE_3P_HARD_DISMISS_TRAIT_MAP: Record<ThirdPartyInviteType, string> = {
	slack: TRAIT_NAMES_HARD_DISMISS.INVITE_ON_3P_SLACK,
	mailto: TRAIT_NAMES_HARD_DISMISS.INVITE_ON_3P_MAILTO,
};

/**
 * Get the current numeric dismiss type for a 3P-within-1P invite type.
 *
 * Resolution order (mirrors Jira's getInviteOn3pState):
 *   1. localStorage value (via DismissState3pIn1p) — authoritative, set on each dismiss.
 *   2. TAP hard-dismiss trait — stored as a numeric string (e.g. '3').
 *   3. Default: 0 (initial, never shown).
 *
 * Numeric scale: 0=initial, 1=soft dismissed once, 2=soft dismissed again,
 * 3=permanently dismissed (dontShowAgain).
 */
export const getInviteOn3pDismissType = (
	type: ThirdPartyInviteType,
	storedDismissState: DismissState3pIn1p,
	traits: IntegrationSpotlightTraits,
): number => {
	const storedValue = storedDismissState[type];
	if (storedValue !== undefined) {
		return storedValue;
	}
	// Fall back to the TAP hard-dismiss trait stored as a numeric string
	const traitName = INVITE_3P_HARD_DISMISS_TRAIT_MAP[type];
	const traitValue = Number(traits[traitName]);
	return Number.isNaN(traitValue) ? 0 : traitValue;
};

/**
 * Check if the 3P-within-1P invite spotlight is eligible to show.
 *
 * Uses the numeric dismiss type (0–3) from localStorage/traits. A value >= 3
 * means permanently dismissed (dontShowAgain). Values 0–2 are always eligible.
 *
 * Note: Stage-based cooldown windows (7/30/90 days) are intentionally not
 * enforced here. The dismiss state is the sole eligibility gate for now.
 */
export const is3pInviteEligible = (
	type: ThirdPartyInviteType,
	traits: IntegrationSpotlightTraits,
	storedDismissState: DismissState3pIn1p,
): boolean => {
	const dismissType = getInviteOn3pDismissType(type, storedDismissState, traits);

	// Permanently dismissed — never show again
	if (dismissType >= 3) {
		return false;
	}

	return true;
};
